import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Box, HStack, IconButton } from "@chakra-ui/react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const MenuBar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <HStack
      spacing={1}
      p={2}
      bg="gray.700"
      borderTopRadius="md"
      borderBottom="1px solid"
      borderColor="gray.600"
    >
      <IconButton
        aria-label="Bold"
        size="sm"
        variant={editor.isActive("bold") ? "primary" : "action"}
        onClick={() => editor.chain().focus().toggleBold().run()}
        icon={<span style={{ fontWeight: "bold" }}>B</span>}
      />
      <IconButton
        aria-label="Italic"
        size="sm"
        variant={editor.isActive("italic") ? "primary" : "action"}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        icon={<span style={{ fontStyle: "italic" }}>I</span>}
      />
      <IconButton
        aria-label="Strike"
        size="sm"
        variant={editor.isActive("strike") ? "primary" : "action"}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        icon={<span style={{ textDecoration: "line-through" }}>S</span>}
      />
      <Box w="1px" h="20px" bg="gray.600" mx={1} />
      <IconButton
        aria-label="Bullet List"
        size="sm"
        variant={editor.isActive("bulletList") ? "primary" : "action"}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        icon={<span>•</span>}
      />
      <IconButton
        aria-label="Ordered List"
        size="sm"
        variant={editor.isActive("orderedList") ? "primary" : "action"}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        icon={<span>1.</span>}
      />
    </HStack>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor",
      },
    },
  });

  // Update editor content when value changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  return (
    <Box>
      <style>
        {`
          .tiptap-editor {
            background-color: #2D3748;
            color: #E2E8F0;
            padding: 12px;
            min-height: 120px;
            border-radius: 0 0 6px 6px;
            outline: none;
          }
          .tiptap-editor:focus {
            box-shadow: 0 0 0 1px #6366f1;
          }
          .tiptap-editor p {
            margin: 0 0 0.5em 0;
          }
          .tiptap-editor p:last-child {
            margin-bottom: 0;
          }
          .tiptap-editor ul,
          .tiptap-editor ol {
            padding-left: 1.5em;
            margin: 0.5em 0;
          }
          .tiptap-editor li {
            margin-bottom: 0.25em;
          }
          .tiptap-editor p.is-editor-empty:first-child::before {
            content: attr(data-placeholder);
            color: #718096;
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}
      </style>
      <Box border="1px solid" borderColor="gray.600" borderRadius="md">
        <MenuBar editor={editor} />
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
